import { useEffect } from 'react';
import { useStore } from '../stores/useStore';
import { getAuthState, checkUrlForToken, getStoredToken, clearStoredToken } from '../lib/api';

/**
 * Hook to initialize and manage authentication state.
 * Call this once at the app root level.
 */
export function useAuthInit() {
  const { setAuthState, setAuthLoading } = useStore();

  useEffect(() => {
    async function checkAuth() {
      // Check if we just came back from OAuth with a token in URL
      checkUrlForToken();

      // If we have a stored token, verify it
      const token = getStoredToken();
      if (!token) {
        setAuthState(null, []);
        return;
      }

      try {
        const state = await getAuthState();
        if (state.authenticated && state.user) {
          setAuthState(state.user, state.organizations);
        } else {
          // Token invalid, clear it
          clearStoredToken();
          setAuthState(null, []);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        clearStoredToken();
        setAuthState(null, []);
      }
    }

    checkAuth();
  }, [setAuthState, setAuthLoading]);
}

/**
 * Hook to access current auth state.
 */
export function useAuth() {
  const authUser = useStore((s) => s.authUser);
  const organizations = useStore((s) => s.organizations);
  const isAuthenticated = useStore((s) => s.isAuthenticated);
  const authLoading = useStore((s) => s.authLoading);
  const clearAuth = useStore((s) => s.clearAuth);

  return {
    user: authUser,
    organizations,
    isAuthenticated,
    isLoading: authLoading,
    clearAuth,
  };
}
